import { useState, useMemo, useContext, useCallback } from 'react';
import { DateTime } from 'luxon';
import { scaleOffsets } from '~/modules/resource-management/ResourcingOverview/util';
import { ResourceOverviewToolbarContext } from '~/modules/resource-management/ResourcingOverview/hooks';
import useDateState from '~/modules/common/charts/timeline/calculations/useDateState';
import { useMeContext } from '~/modules/me';
import {
  getUserPreferenceWeekStart,
  getUserPreferenceWeekEnd
} from '~/modules/common/dates/dayOfWeek';
import { DEFAULT_OVERVIEW_UNIT } from '~/modules/resource-management/common/enums';
import {
  PERIOD_SCALE_ENUM,
  DEFAULT_PERIOD_SCALE
} from '~/modules/resourcing/common/enums';
import { usePutInsightsSettings } from './useInsightsSettings';

export const LUXON_SCALE = {
  [PERIOD_SCALE_ENUM.DAYS]: 'day',
  [PERIOD_SCALE_ENUM.WEEKS]: 'week',
  [PERIOD_SCALE_ENUM.MONTHS]: 'month',
  [PERIOD_SCALE_ENUM.QUARTERS]: 'quarter',
  [PERIOD_SCALE_ENUM.YEARS]: 'year'
};

export const useResourceOverviewToolbarContext = () =>
  useContext(ResourceOverviewToolbarContext);

export const useResourceManagementDashboardContextValue = ({
  insightsSettings
} = {}) => {
  const me = useMeContext();
  const { putInsightsSettings } = usePutInsightsSettings();
  const [periodScale, setPeriodScaleState] = useState(
    insightsSettings?.periodScale || DEFAULT_PERIOD_SCALE
  );
  const [displayUnit, setDisplayUnitState] = useState(
    insightsSettings?.displayUnit || DEFAULT_OVERVIEW_UNIT
  );

  const setPeriodScale = useCallback(
    newScale => {
      setPeriodScaleState(newScale);
      if (putInsightsSettings) {
        putInsightsSettings({
          displayUnit,
          periodScale: newScale
        });
      }
    },
    [displayUnit, putInsightsSettings]
  );

  const setDisplayUnit = useCallback(
    newUnit => {
      setDisplayUnitState(newUnit);
      if (putInsightsSettings) {
        putInsightsSettings({
          displayUnit: newUnit,
          periodScale
        });
      }
    },
    [periodScale, putInsightsSettings]
  );

  const [startDate, setStartDate] = useDateState({
    initialValue: DateTime.utc(),
    sessionStorageKey: 'resource-management-dashboard-start-date'
  });

  const dateRange = useMemo(() => {
    if (periodScale === PERIOD_SCALE_ENUM.WEEKS) {
      const weekStartDate = getUserPreferenceWeekStart(me, startDate);
      const weekEndDate = getUserPreferenceWeekEnd(me, startDate);

      return {
        startDate: weekStartDate,
        endDate: weekEndDate
      };
    }

    const luxonScale = LUXON_SCALE[periodScale];

    return {
      startDate: startDate.startOf(luxonScale),
      endDate: startDate.endOf(luxonScale)
    };
  }, [startDate, periodScale, me]);

  const onPreviousClick = useCallback(() => {
    const offsetToApply = scaleOffsets[periodScale];

    setStartDate(startDate.minus(offsetToApply));
  }, [periodScale, setStartDate, startDate]);

  const onNextClick = useCallback(() => {
    const offsetToApply = scaleOffsets[periodScale];

    setStartDate(startDate.plus(offsetToApply));
  }, [periodScale, setStartDate, startDate]);

  const setDate = useCallback(
    newDate => {
      setStartDate(newDate);
    },
    [setStartDate]
  );

  return useMemo(
    () => ({
      periodScale,
      setPeriodScale,
      displayUnit,
      setDisplayUnit,
      dateRange,
      onPreviousClick,
      onNextClick,
      setDate
    }),
    [
      periodScale,
      setPeriodScale,
      displayUnit,
      setDisplayUnit,
      dateRange,
      onPreviousClick,
      onNextClick,
      setDate
    ]
  );
};

export default useResourceOverviewToolbarContext;
